import React from "react"
import Chart from "react-apexcharts"
import { getWeekTooltipLabel } from "utils/dateUtil"

const CHART_COLORS = [
  "#A78BFA",
  "#34D399",
  "#FB923C",
  "#F87171",
  "#FCD34D",
  "#2DD4BF",
  "#F472B6",
  "#60A5FA",
  "#818CF8",
  "#F59E0B",
]

/**
 * AreaChart — supports single OR multi-line series.
 *
 * Props:
 *  title        string
 *  categories   string[]          — x-axis labels (dates etc.)
 *  series       { name, data[] }[] — one object per line
 *  pound        bool              — prefix values with £
 *  colorIndex   number            — shifts the starting color (default 0)
 */
const AreaChart = ({
  title,
  categories,
  series = [],
  pound,
  colorIndex = 0,
  onPointClick,
}) => {
  if (!series.length || !categories?.length) {
    return (
      <div
        style={{
          background: "linear-gradient(145deg, #ffffff 0%, #f8faff 100%)",
          borderRadius: "16px",
          border: "1px solid #e2e8f022",
          boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
          position: "relative",
          overflow: "hidden",
          marginBottom: "1.5rem",
          minHeight: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#94a3b8",
          fontSize: "14px",
        }}
      >
        No data available
      </div>
    )
  }

  const isMulti = series.length > 1

  // Build a colour per series, starting from colorIndex
  const seriesColors = series.map(
    (_, i) => CHART_COLORS[(colorIndex + i) % CHART_COLORS.length]
  )

  // Primary accent colour (first series)
  const primaryColor = seriesColors[0]

  // Weekly label logic — only when > 10 points
  const useWeeklyLabels = true
  const actualDates = categories ? [...categories] : []
  const xCategories = useWeeklyLabels
    ? categories.map((_, i) => `${i + 1}`)
    : categories

  const getFormatter = val => {
    if (!val && val !== 0) return "N/A"
    return pound
      ? `£${Math.round(val).toLocaleString("en-GB")}`
      : Number(val).toLocaleString("en-GB")
  }

  const options = {
    chart: {
      type: "area",
      toolbar: { show: false },
      background: "transparent",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: { enabled: true, delay: 100 },
      },
      dropShadow: {
        enabled: !isMulti,
        color: primaryColor,
        top: 4,
        blur: 10,
        opacity: 0.12,
      },
    },

    stroke: {
      curve: "smooth",
      width: isMulti ? 2 : 3,
      colors: seriesColors,
    },

    markers: {
      size: isMulti ? 3 : 5,
      colors: seriesColors,
      strokeColors: ["#fff"],
      strokeWidth: 2,
      hover: { size: isMulti ? 5 : 8 },
    },

    dataLabels: { enabled: false },

    fill: isMulti
      ? {
          // Lighter fill for multi-line so lines stay readable
          type: "gradient",
          gradient: {
            type: "vertical",
            shadeIntensity: 0.5,
            opacityFrom: 0.15,
            opacityTo: 0,
            stops: [0, 100],
          },
        }
      : {
          type: "gradient",
          gradient: {
            type: "vertical",
            shadeIntensity: 1,
            gradientToColors: [`${primaryColor}00`],
            inverseColors: false,
            opacityFrom: 0.45,
            opacityTo: 0,
            stops: [0, 100],
          },
        },

    colors: seriesColors,

    xaxis: {
      categories: xCategories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: -45,
        style: { colors: "#94a3b8", fontSize: "12px", fontWeight: 500 },
      },
    },

    yaxis: {
      labels: {
        style: { colors: "#64748b", fontSize: "11px", fontWeight: 600 },
        formatter: val => getFormatter(val),
      },
    },

    legend: {
      show: isMulti,
      position: "top",
      horizontalAlign: "right",
      fontSize: "11px",
      fontWeight: 600,
      height: "120px",
      labels: { colors: "#111 !importent" },
      markers: { radius: 3, width: 10, height: 3 },
      itemMargin: { horizontal: 8 },
    },

    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 16, bottom: 0, left: 8 },
    },

    tooltip: {
      theme: "dark",
      shared: isMulti, // shared tooltip shows all series at once on multi-line
      intersect: false,
      style: { fontSize: "12px" },
      x: {
        formatter: (val, { dataPointIndex }) =>
          getWeekTooltipLabel(dataPointIndex, actualDates[dataPointIndex]),
      },
      y: { formatter: val => getFormatter(val) },
    },

    states: {
      hover: { filter: { type: "lighten", value: 0.08 } },
      active: { filter: { type: "darken", value: 0.1 } },
    },
  }

  return (
    <div
      style={{
        background: "linear-gradient(145deg, #ffffff 0%, #f8faff 100%)",
        borderRadius: "16px",
        border: `1px solid ${primaryColor}22`,
        boxShadow: `0 4px 24px ${primaryColor}14, 0 1px 4px rgba(0,0,0,0.04)`,
        position: "relative",
        overflow: "hidden",
        marginBottom: "1.5rem",
      }}
    >
      {/* Top accent bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}88)`,
          borderRadius: "16px 16px 0 0",
        }}
      />

      <div style={{ padding: "20px 16px 8px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "4px",
            padding: "0 8px",
          }}
        >
          <div
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: primaryColor,
              boxShadow: `0 0 8px ${primaryColor}88`,
            }}
          />
          <h4
            style={{
              margin: 0,
              fontSize: "15px",
              fontWeight: 700,
              color: "#1e293b",
            }}
          >
            {title}
          </h4>
        </div>

        <Chart options={options} series={series} type="area" height={450} />
      </div>
    </div>
  )
}

export default AreaChart
